import type { JobCardDataFragment } from "../../../graphQL/fragments/__generated__/jobCardData.gqlgenerated";
import type { AppearanceMode } from "../JobCard";
import type { GettextInterface } from "@indeed/indeed-gettext";

import { Text, Box } from "../../../../mads";
import {
  getCommuteText,
  getLocationLabel,
  LocationComponent,
} from "../LocationComponent/LocationComponent";

import { Platform, StyleSheet } from "react-native";

const REMOTE_WORK_ATTR = "DSQF7";
const TEMPORARILY_REMOTE_WORK_ATTR = "VAMUB";
const MAX_COMMUTE_MINUTES = 120;

export function getDisplayedCommuteMinutes(
  job: Pick<JobCardDataFragment, "commute" | "attributes">,
): number | undefined {
  const attributeKeys = job.attributes.map((attr) => attr.key);
  const durationMinutes = job.commute?.durationMinutes;

  return durationMinutes &&
    durationMinutes > 0 &&
    durationMinutes <= MAX_COMMUTE_MINUTES &&
    !attributeKeys.includes(REMOTE_WORK_ATTR) &&
    !attributeKeys.includes(TEMPORARILY_REMOTE_WORK_ATTR)
    ? durationMinutes
    : undefined;
}

export function getLocationAccessibilityText({
  job,
  i18n,
}: {
  job: Pick<JobCardDataFragment, "commute" | "attributes" | "location">;
  i18n: GettextInterface;
}): string {
  const commuteTimeMinutes = getDisplayedCommuteMinutes(job);

  return [
    getCommuteText(i18n, commuteTimeMinutes),
    getLocationLabel(i18n, job.location.formatted, commuteTimeMinutes, job.attributes),
  ]
    .filter(Boolean)
    .join(", ");
}

export type CompanyInfoProps = {
  job: JobCardDataFragment;
  appearanceMode: AppearanceMode;
};

export function CompanyInfo({ job, appearanceMode }: CompanyInfoProps) {
  const commuteTime = getDisplayedCommuteMinutes(job);

  return (
    <Box sx={styles.root}>
      <Box
        sx={styles.horizontal}
        {...Platform.select({
          ios: { accessible: true },
          android: { screenReaderFocusable: true },
        })}
      >
        <Text sx={styles.companyName}>{job.sourceEmployerName}</Text>
      </Box>
      <LocationComponent
        accessible={false}
        jobKey={job.key}
        commuteTimeMinutes={commuteTime}
        location={job.location.formatted}
        appearanceMode={appearanceMode}
        attributes={job.attributes}
      />
    </Box>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  companyName: {
    flexShrink: 1,
  },
  horizontal: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
});
